/**
 * Boot-to-milestone perf timing: marks named points once each and emits
 * `perf-*` analytics events with elapsed ms.
 *
 * `appStartTime` is set when this module loads (or reset by `recordAppStart`);
 * each milestone fires `markPoint(name)` to log its delta from start. The
 * `recorded` map ensures each named point fires at most once per session —
 * repeat calls are silent. Module-level side effects wire the auth state change
 * handler (so `auth-known` and `auth-user` mark automatically) and the
 * parent-window `psi-sidebar-start-time` message (so embedded instances can
 * compute `sidebar-start` against the outer page's clock instead of the
 * iframe's). Callers may pass a null `Datastore` because some milestones happen
 * before one exists.
 */

import { onUserChanged } from 'util/auth-client';

import { trackEvent } from './analytics';
import type { Datastore } from './datastore';

let appStartTime = Date.now();
const recorded: Record<string, boolean> = {};

export function recordAppStart() {
    appStartTime = Date.now();
}

export function getAppStartTime() {
    return appStartTime;
}

export function markPoint(datastore: Datastore | null, name: string) {
    if (!recorded[name]) {
        recorded[name] = true;
        const diff = Date.now() - appStartTime;
        trackEvent(datastore, 'perf-' + name, { ms: diff });
    }
}

export function markSidebarStart(datastore: Datastore | null, sidebarStartTime: number) {
    if (!recorded['sidebar-start']) {
        recorded['sidebar-start'] = true;
        const diff = appStartTime - sidebarStartTime;
        trackEvent(datastore, 'perf-sidebar-start', { ms: diff });
    }
}

export function _resetForTest() {
    Object.keys(recorded).forEach((key) => delete recorded[key]);
}

onUserChanged((user) => {
    markPoint(null, 'auth-known');
    if (user) {
        markPoint(null, 'auth-user');
    }
});

if (typeof window !== 'undefined') {
    window.addEventListener('message', (event) => {
        if (event?.data?.type === 'psi-sidebar-start-time') {
            markSidebarStart(null, event.data.startTime);
        }
    });
}
